// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState } from 'react';
import { number, shape, string, bool, func, node } from 'prop-types';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { getI18n } from '../../../utils/intl';
import {
  Typography,
  withStyles,
  Button,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';

import styles from './styles';
import FormTextField from '../../FormTextField';
import BreadcrumbsForPages from "../../../pageComponents/BreadcrumbsForPages";
import { get } from "lodash";
import { connect } from "react-redux";
import compose from 'recompose/compose';

export const DetailsHeader = (props) => {
  const {
    classes,
    deleteButtonLabel,
    deleteFn,
    handleChange,
    headerId,
    helperText,
    isFieldEdit,
    isFieldEditIcon,
    isNameError,
    marker='',
    maxLength,
    name,
    onClickEditIcon,
    optionalButtonClass,
    optionalButtonLabel,
    optionalButtonOnClick,
    placeHolderText,
    showDelete = false,
    showOptionalButton = false,
    statusComponent = <span/>,
    subTitle = '',
    subTitleComponent= <span/>,
    textFieldClass = '',
    type,
    pathName,
    showBreadCrumbs = false,
    hideTextLabel = false,
  } = props;

  const [isDeleteClicked , setIsDeleteClicked] = useState(false);
  const intl = getI18n(useIntl());
  const dButtonLabel = deleteButtonLabel ? deleteButtonLabel : intl.getI18nFormattedMessage('label.details.header.delete');

  return (
    <div className={classes.topContainer}>
      <div
        className={classes.headerContainer}
        id={headerId} data-apim-test={headerId} data-layer7-test={headerId}
      >
        {showBreadCrumbs &&
          <BreadcrumbsForPages pathName={pathName} items={[]} classes={classes}
                               pageContainerClass={classes.pageBreadCrumbClass}/>
        }
        {type === "textField" && isFieldEdit ?
          <FormTextField
            error={isNameError}
            fieldContainerClass={classes.textFieldContainer}
            handleChange={(value) => handleChange(value)}
            helperText={helperText}
            id={`${headerId}-textField`}
            maxLength={maxLength ? maxLength : 255}
            name={''}
            placeholder={placeHolderText}
            textFieldClass={clsx(classes.textFieldClass, textFieldClass)}
            value={name}
            hideLabel={hideTextLabel}
          />
        :
            <Typography variant="h1" className={classes.pageTitle}>
              {name}
              {type === "textField" && isFieldEditIcon &&
                <IconButton aria-label="edit" className={classes.editIcon} onClick={onClickEditIcon}>
                  <EditIcon />
                </IconButton>
              }
            </Typography>
        }
        {subTitle &&
          <div className={classes.subTitle}>
            {subTitle}
            {marker && <span className={classes.markerContainer}>{marker}</span>}
          </div>
        }
        {statusComponent}
        {subTitleComponent}
      </div>
      <div className={classes.deleteContainer}>
        {showOptionalButton &&
          <Button
            id={`${headerId}-optional-button`}
            data-apim-test={`${headerId}-optional-button`}
            color="primary"
            variant="outlined"
            className={optionalButtonClass}
            onClick={() => optionalButtonOnClick()}
          >
            {optionalButtonLabel}
          </Button>
        }
      { showDelete &&
            (isDeleteClicked ?
            <div className={classes.deleteConfirmContainer}>
              <Button
                id="delete-confirm-button"
                data-apim-test="delete-confirm-button"
                className={classes.errorBackgroundButton}
                onClick={() => {
                              setIsDeleteClicked(false);
                              deleteFn();
                            }
                        }
                >
                {intl.getI18nMessage('label.details.header.delete.confirm.status.button')}
              </Button>
              <Button
                id="delete-cancel-button"
                data-apim-test="delete-cancel-button"
                color="primary"
                variant="outlined"
                className={classes.cancelIcon}
                onClick={() => setIsDeleteClicked(false)}
                >
                {intl.getI18nFormattedMessage('label.details.header.delete.cancel.delete')}
              </Button>
            </div> :
            <Button
              id="delete-button"
              data-apim-test="delete-button"
              variant="outlined"
              className={classes.deleteButton}
              onClick={() => setIsDeleteClicked(true)}
            >
              {dButtonLabel}
            </Button>
          )}
      </div>
    </div>
  );
};

DetailsHeader.propTypes = {
  classes: shape({}),
  deleteButtonLabel: string,
  deleteFn: func,
  handleChange: func,
  headerId: string,
  helperText: string,
  isFieldEdit: bool,
  isFieldEditIcon: bool,
  isNameError: bool,
  marker: string,
  maxLength: number,
  modifyTs: number,
  name: string,
  onClickEditIcon: func,
  optionalButtonClass: shape({}),
  optionalButtonLabel: string,
  optionalButtonOnClick: func,
  placeHolderText: string,
  showDelete: bool,
  showOptionalButton: bool,
  status: string,
  statusColorClass: shape({}),
  statusComponent: node,
  subTitle: string,
  subTitleComponent: node,
  textFieldClass: string,
  type: string,
  pathName: string,
  showBreadCrumbs: bool,
  hideTextLabel: bool,
};

export const mapStateToProps = (state) => ({
  pathName: get(state, 'router.location.pathname'),
});

DetailsHeader.displayName = 'DetailsHeader';

export default compose(
  withStyles(styles),
  connect(mapStateToProps),
)(DetailsHeader);
